/**
 * Native window chrome for the Mac shell.
 *
 * Overlay title bars hide the system title, so Mission Control and the Window
 * menu only stay honest if we keep pushing the document name through. The
 * traffic-light dirty indicator is a separate bit on NSWindow — a bullet in
 * the title is not the same thing, and is what the close button uses to warn
 * before discarding unsaved work.
 */

import { invoke } from "@tauri-apps/api/core";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { APP_NAME } from "@/lib/brand";
import { isDesktopApp, isPresenterSurface } from "@/lib/runtime";

export { isPresenterSurface };

export async function revealMainWindow() {
  if (!isDesktopApp) return;
  const window = getCurrentWindow();
  await window.show();
  await window.unminimize();
  await window.setFocus();
}

export async function hideMainWindow() {
  if (!isDesktopApp) return;
  await getCurrentWindow().hide();
}

export async function setMainWindowTitle(title: string, dirty: boolean) {
  if (!isDesktopApp || isPresenterSurface()) return;
  const window = getCurrentWindow();
  const trimmed = title.trim() || "Untitled";
  await window.setTitle(dirty ? `${trimmed} — Edited` : trimmed);
  try {
    await invoke("set_document_edited", { edited: dirty });
  } catch {
    // The command is Mac-only; a missing handler must not break the editor.
  }
}

export async function resetMainWindowTitle() {
  await setMainWindowTitle(APP_NAME, false);
}
